/**
 * Class strings for the plain-button menus: `KebabMenu` (every kebab in the app), SectionList
 * (left sidebar) and RightSidebar.
 *
 * The geometry comes from `@/fetch-ui/_logic/menu-styles`, shared with the Radix menus and with
 * `FetchToolbarSelect`'s options, so a menu and a toolbar dropdown read as the same surface. Only
 * the state hook differs here: these rows highlight on `hover:` because their keyboard navigation
 * moves DOM focus, whereas Radix rows highlight on `data-[highlighted]`.
 */

import { MENU_ROW_BASE_CLASS, MENU_ROW_PX_CLASS } from "@/fetch-ui/_logic/menu-styles";
import { cn } from "@/lib/utils/index";

/** Every row in this family is a full-width button whose label must not wrap. */
const PLAIN_ROW_BASE = cn(MENU_ROW_BASE_CLASS, MENU_ROW_PX_CLASS, "w-full whitespace-nowrap");

/** Base class for enabled menu items */
export const MENU_ITEM_CLASS = cn(PLAIN_ROW_BASE, "cursor-default hover:bg-gray-100 hover:text-gray-900");

/** Base class for disabled menu items */
export const DISABLED_MENU_ITEM_CLASS = cn(PLAIN_ROW_BASE, "cursor-not-allowed text-gray-300");

/** Base class for destructive (delete) menu items. `red-600` rather than `red-500`: #ef4444 is
 *  ~3.8:1 on white, under the 4.5:1 text floor, while #dc2626 clears it at ~4.9:1. */
export const DESTRUCTIVE_MENU_ITEM_CLASS = cn(
  PLAIN_ROW_BASE,
  "cursor-default text-red-600 hover:bg-red-50 hover:text-red-700"
);

/** Shared framer-motion animation props for dropdown/context menus */
export const MENU_ANIMATION_PROPS = {
  initial: { opacity: 0, scale: 0.95, y: -4 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: -4 },
  transition: { duration: 0.15 },
} as const;
